import React, { useEffect, useState } from 'react';
import { Loan, Role, ColumnKey, ROLE_COLUMNS, COLUMN_LABELS, monthOrder, stageOrder } from '../data';
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ExternalLink, AlertTriangle, Edit2, Check } from 'lucide-react';
interface PipelineTableProps {
  loans: Loan[];
  role: Role;
  /** Optional override of the role's default visible columns. */
  visibleColumns?: ColumnKey[];
  /** Whether the current role is allowed to clear an active SOS flag. */
  canClearSos: boolean;
  onUpdateLoan: (id: string, field: keyof Loan, value: any) => void;
  onToggleSos: (loan: Loan) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Bumping this resets sort to default. */
  sortResetKey: number;
  /** Color-band thresholds for the Days in Pipe badge. */
  daysInPipeThresholds?: {
    good: number;
    warning: number;
  };
}
type SortField = ColumnKey | 'default';
/** Columns that are NOT sortable. */
const NON_SORTABLE: ColumnKey[] = ['processorNote', 'sos'];
const TEXT_FIELDS: ColumnKey[] = ['loanName', 'loanOwner', 'teamLead', 'processor', 'processorAssistant'];
export function PipelineTable({
  loans,
  role,
  visibleColumns: visibleColumnsProp,
  canClearSos,
  onUpdateLoan,
  onToggleSos,
  page,
  pageSize,
  onPageChange,
  sortResetKey,
  daysInPipeThresholds = {
    good: 15,
    warning: 30
  }
}: PipelineTableProps) {
  const [sortField, setSortField] = useState<SortField>('default');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{
    note: string;
    month: string;
  }>({
    note: '',
    month: ''
  });
  // Reset sort to default when sortResetKey changes
  useEffect(() => {
    setSortField('default');
    setSortDirection('asc');
  }, [sortResetKey]);
  const visibleColumns = visibleColumnsProp ?? ROLE_COLUMNS[role];
  const handleSort = (field: ColumnKey) => {
    if (NON_SORTABLE.includes(field)) return;
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Default multi-sort: Month Pushing (current→future) then Stage (closest to funding)
  const defaultCompare = (a: Loan, b: Loan) => {
    const mDiff = monthOrder(a.monthPushing) - monthOrder(b.monthPushing);
    if (mDiff !== 0) return mDiff;
    return stageOrder(a.stage) - stageOrder(b.stage);
  };
  const sortedLoans = [...loans].sort((a, b) => {
    if (sortField === 'default') return defaultCompare(a, b);
    const modifier = sortDirection === 'asc' ? 1 : -1;
    if (sortField === 'monthPushing') {
      return (monthOrder(a.monthPushing) - monthOrder(b.monthPushing)) * modifier;
    }
    if (sortField === 'stage') {
      return (stageOrder(a.stage) - stageOrder(b.stage)) * modifier;
    }
    const aVal = a[sortField as keyof Loan];
    const bVal = b[sortField as keyof Loan];
    if (aVal === bVal) return 0;
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal) * modifier;
    }
    return (aVal as number < (bVal as number) ? -1 : 1) * modifier;
  });
  const totalPages = Math.max(1, Math.ceil(sortedLoans.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, sortedLoans.length);
  const pagedLoans = sortedLoans.slice(startIdx, endIdx);
  const startEdit = (loan: Loan) => {
    setEditingId(loan.id);
    setEditValues({
      note: loan.processorNote,
      month: loan.monthPushing
    });
  };
  const saveEdit = (id: string) => {
    onUpdateLoan(id, 'processorNote', editValues.note);
    onUpdateLoan(id, 'monthPushing', editValues.month);
    setEditingId(null);
  };
  const getDaysColor = (days: number) => {
    if (days < daysInPipeThresholds.good) return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
    if (days <= daysInPipeThresholds.warning) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  };
  const SortIcon = ({
    field
  }: {
    field: ColumnKey;
  }) => {
    if (sortField !== field) return <ChevronDown className="w-3 h-3 opacity-20" data-id="element-327" />;
    return sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" data-id="element-328" /> : <ChevronDown className="w-3 h-3" data-id="element-329" />;
  };
  const renderHeader = (col: ColumnKey) => {
    const sortable = !NON_SORTABLE.includes(col);
    const isRight = col === 'loanAmount';
    return <th key={col} onClick={() => sortable && handleSort(col)} className={`px-3 py-2.5 text-xs font-semibold text-slate-600 dark:text-slate-300 select-none whitespace-nowrap ${isRight ? 'text-right' : 'text-left'} ${sortable ? 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors' : ''} ${col === 'sos' ? 'w-10 text-center' : ''} ${col === 'processorNote' ? 'w-64' : ''}`} data-id="element-330">
        <div className={`flex items-center space-x-1 ${isRight ? 'justify-end' : ''} ${col === 'sos' ? 'justify-center' : ''}`} data-id="element-331">
          <span data-id="element-332">{COLUMN_LABELS[col]}</span>
          {sortable && <SortIcon field={col} data-id="element-333" />}
        </div>
      </th>;
  };
  const renderCell = (col: ColumnKey, loan: Loan, isEditing: boolean) => {
    switch (col) {
      case 'sos':
        {
          const locked = loan.isSos && !canClearSos;
          return <td key={col} className="px-3 py-2.5 text-center relative" data-id="element-334">
            {loan.isSos && <div className="absolute left-0 top-0 bottom-0 w-1 bg-sos-500" data-id="element-335"></div>}
            <button onClick={() => onToggleSos(loan)} disabled={locked} aria-label={loan.isSos ? canClearSos ? 'Clear SOS' : 'SOS flag — only Team Lead or Admin can clear' : 'Flag SOS'} title={locked ? 'Only a Team Lead or Admin can clear an SOS flag' : undefined} className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${loan.isSos ? 'bg-sos-500 border-sos-500 text-white' : 'border-slate-300 dark:border-slate-600 text-transparent hover:border-sos-400'} ${locked ? 'cursor-not-allowed opacity-90' : ''}`} data-id="element-336">
              <AlertTriangle className="w-3.5 h-3.5" data-id="element-337" />
            </button>
          </td>;
        }
      case 'daysInPipe':
        return <td key={col} className="px-3 py-2.5 whitespace-nowrap" data-id="element-338">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDaysColor(loan.daysInPipe)}`} data-id="element-339">
              {loan.daysInPipe}
            </span>
          </td>;
      case 'monthPushing':
        return <td key={col} className="px-3 py-2.5 whitespace-nowrap" data-id="element-340">
            {isEditing ? <select value={editValues.month} onChange={e => setEditValues({
            ...editValues,
            month: e.target.value
          })} className="text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-1 py-0.5 focus:ring-1 focus:ring-cobalt-500 outline-none" data-id="element-341">
                {['May', 'June', 'July', 'August', 'September'].map(m => <option key={m} data-id="element-342">{m}</option>)}
              </select> : <span className="text-slate-700 dark:text-slate-300" data-id="element-343">
                {loan.monthPushing}
              </span>}
          </td>;
      case 'loanName':
        return <td key={col} className="px-3 py-2.5 whitespace-nowrap" data-id="element-344">
            <a href="#" target="_blank" rel="noopener noreferrer" className="font-medium text-cobalt-600 dark:text-cobalt-400 hover:underline inline-flex items-center group-hover:text-cobalt-700 dark:group-hover:text-cobalt-300" data-id="element-345">
              {loan.loanName}
              <ExternalLink className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-346" />
            </a>
            {loan.isNewApp && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-cobalt-100 text-cobalt-800 dark:bg-cobalt-900/30 dark:text-cobalt-300" data-id="element-347">
                NEW
              </span>}
          </td>;
      case 'stage':
        return <td key={col} className="px-3 py-2.5 whitespace-nowrap" data-id="element-348">
            <span className="text-slate-600 dark:text-slate-400 text-xs border border-slate-200 dark:border-slate-700 px-2 py-1 rounded-md bg-white dark:bg-slate-800" data-id="element-349">
              {loan.stage}
            </span>
          </td>;
      case 'processorNote':
        return <td key={col} className="px-3 py-2.5" data-id="element-350">
            {isEditing ? <input type="text" value={editValues.note} onChange={e => setEditValues({
            ...editValues,
            note: e.target.value
          })} className="w-full text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-2 py-1 focus:ring-1 focus:ring-cobalt-500 outline-none" autoFocus onKeyDown={e => e.key === 'Enter' && saveEdit(loan.id)} data-id="element-351" /> : <div className="text-slate-600 dark:text-slate-300 truncate max-w-[250px] cursor-pointer hover:text-slate-900 dark:hover:text-white" onClick={() => startEdit(loan)} title={loan.processorNote} data-id="element-352">
                {loan.processorNote || <span className="text-slate-400 italic" data-id="element-353">Add note…</span>}
              </div>}
          </td>;
      case 'loanAmount':
        return <td key={col} className="px-3 py-2.5 whitespace-nowrap text-right font-medium text-slate-900 dark:text-slate-100" data-id="element-354">
            ${loan.loanAmount.toLocaleString()}
          </td>;
      default:
        return <td key={col} className="px-3 py-2.5 whitespace-nowrap text-slate-700 dark:text-slate-300" data-id="element-355">
            {loan[col as keyof Loan] as string}
          </td>;
    }
  };
  return <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col h-full" data-id="element-356">
      <div className="flex-1 overflow-auto" data-id="element-357">
        <table className="w-full text-sm text-left" data-id="element-358">
          <thead className="text-xs text-slate-700 uppercase bg-slate-50 dark:bg-slate-800/80 dark:text-slate-300 sticky top-0 z-20 shadow-sm backdrop-blur-sm" data-id="element-359">
            <tr data-id="element-360">
              {visibleColumns.map(renderHeader)}
              <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-600 dark:text-slate-300" data-id="element-361">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700/50" data-id="element-362">
            {pagedLoans.length === 0 && <tr data-id="element-363">
                <td colSpan={visibleColumns.length + 1} className="px-3 py-10 text-center text-sm text-slate-500 dark:text-slate-400" data-id="element-364">
                  No loans match the current filters.
                </td>
              </tr>}
            {pagedLoans.map(loan => {
            const isEditing = editingId === loan.id;
            return <tr key={loan.id} className={`group transition-colors ${loan.isSos ? 'bg-sos-50/50 dark:bg-sos-900/10 hover:bg-sos-50 dark:hover:bg-sos-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'}`} data-id="element-365">
                  {visibleColumns.map(col => renderCell(col, loan, isEditing))}
                  <td className="px-3 py-2.5 whitespace-nowrap text-right" data-id="element-366">
                    {isEditing ? <button onClick={() => saveEdit(loan.id)} className="p-1 text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/30 rounded transition-colors" data-id="element-367">
                        <Check className="w-4 h-4" data-id="element-368" />
                      </button> : <button onClick={() => startEdit(loan)} className="p-1 text-slate-400 hover:text-cobalt-600 hover:bg-cobalt-50 dark:hover:text-cobalt-400 dark:hover:bg-cobalt-900/30 rounded transition-colors opacity-0 group-hover:opacity-100" data-id="element-369">
                        <Edit2 className="w-4 h-4" data-id="element-370" />
                      </button>}
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      <div className="bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between text-sm" data-id="element-371">
        <div className="text-slate-500 dark:text-slate-400 flex items-center gap-2" data-id="element-372">
          <span data-id="element-373">
            Showing{' '}
            <span className="font-medium text-slate-900 dark:text-white" data-id="element-374">
              {sortedLoans.length === 0 ? 0 : startIdx + 1}
            </span>{' '}
            to{' '}
            <span className="font-medium text-slate-900 dark:text-white" data-id="element-375">
              {endIdx}
            </span>{' '}
            of{' '}
            <span className="font-medium text-slate-900 dark:text-white" data-id="element-376">
              {sortedLoans.length}
            </span>{' '}
            records
          </span>
          <span className="text-slate-400 dark:text-slate-500" data-id="element-377">
            · {pageSize}/page
          </span>
          {sortField === 'default' && <span className="hidden md:inline-flex items-center text-[10px] uppercase font-semibold tracking-wider bg-slate-200/70 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded" data-id="element-378">
              Default sort: Month → Stage
            </span>}
        </div>
        <div className="flex items-center space-x-2" data-id="element-379">
          <button onClick={() => onPageChange(Math.max(1, safePage - 1))} className="inline-flex items-center px-2.5 py-1 border border-slate-200 dark:border-slate-700 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" disabled={safePage <= 1} data-id="element-380">
            <ChevronLeft className="w-4 h-4 mr-0.5" data-id="element-381" />
            Previous
          </button>
          <span className="text-xs text-slate-500 dark:text-slate-400 px-2" data-id="element-382">
            Page{' '}
            <span className="font-semibold text-slate-700 dark:text-slate-200" data-id="element-383">
              {safePage}
            </span>{' '}
            of {totalPages}
          </span>
          <button onClick={() => onPageChange(Math.min(totalPages, safePage + 1))} className="inline-flex items-center px-2.5 py-1 border border-slate-200 dark:border-slate-700 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" disabled={safePage >= totalPages} data-id="element-384">
            Next
            <ChevronRight className="w-4 h-4 ml-0.5" data-id="element-385" />
          </button>
        </div>
      </div>
    </div>;
}